import { useState } from "react";
import { Plus, Trash2, Send, Save } from "lucide-react";
import { toast } from "sonner";
import { api, eur, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";

export function QuoteEditor({ request, quote, onSaved }) {
  const { config } = useAuth();
  const { t } = useT();
  const [items, setItems] = useState(() => (quote?.items?.length ? quote.items : [{ description: t("admin.editor.labor"), qty: 1, unit_price: 0 }]).map((it, i) => ({ _key: `${Date.now()}-${i}`, ...it })));
  const [notes, setNotes] = useState(quote?.notes || "");
  const [busy, setBusy] = useState(false);
  const locked = quote?.status === "accepted";
  const vat = config?.vat_rate ?? 22;
  const subtotal = items.reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.unit_price) || 0), 0);
  const total = subtotal * (1 + vat / 100);
  const credit = request.inspection_paid ? request.inspection_fee : 0;
  const due = Math.max(total - credit, 0);
  const pct = due <= (config?.payment_threshold ?? 1500) ? 100 : (config?.deposit_pct ?? 30);
  const upd = (i, k, v) => setItems(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));

  const save = async (send) => {
    setBusy(true);
    try {
      const payload = { items: items.map((i) => ({ description: i.description, qty: Number(i.qty), unit_price: Number(i.unit_price) })), notes };
      const { data } = await api.post(`/admin/requests/${request.id}/quote`, payload);
      if (send) { await api.post(`/admin/quotes/${data.id}/send`); toast.success(t("admin.editor.sent")); } else toast.success(t("admin.editor.draftSaved"));
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div data-testid="quote-editor">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs uppercase tracking-wider text-ink/50"><th className="pb-2">{t("admin.editor.desc")}</th><th className="pb-2 w-20">{t("quote.qty")}</th><th className="pb-2 w-32">{t("admin.editor.unitPrice")}</th><th className="pb-2 w-28 text-right">{t("quote.total")}</th><th className="w-8"></th></tr></thead>
        <tbody>
          {items.map((it, i) => (
            <tr key={it._key}>
              <td className="py-1 pr-2"><input data-testid={`quote-item-desc-${i}`} className="input-craft !py-2" disabled={locked} value={it.description} onChange={(e) => upd(i, "description", e.target.value)} /></td>
              <td className="py-1 pr-2"><input data-testid={`quote-item-qty-${i}`} type="number" step="0.5" min="0" className="input-craft !py-2 font-mono" disabled={locked} value={it.qty} onChange={(e) => upd(i, "qty", e.target.value)} /></td>
              <td className="py-1 pr-2"><input data-testid={`quote-item-price-${i}`} type="number" step="0.01" min="0" className="input-craft !py-2 font-mono" disabled={locked} value={it.unit_price} onChange={(e) => upd(i, "unit_price", e.target.value)} /></td>
              <td className="py-1 text-right font-mono">{eur((Number(it.qty) || 0) * (Number(it.unit_price) || 0))}</td>
              <td className="py-1 text-right">{!locked && items.length > 1 && <button onClick={() => setItems(items.filter((_, idx) => idx !== i))} className="text-ink/40 hover:text-red-600" data-testid={`quote-item-remove-${i}`}><Trash2 size={14} /></button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!locked && <button data-testid="quote-add-item" onClick={() => setItems([...items, { description: "", qty: 1, unit_price: 0 }])} className="btn-ghost mt-2 -ml-3"><Plus size={14} /> {t("admin.editor.addItem")}</button>}
      <div className="mt-4 grid sm:grid-cols-2 gap-6">
        <textarea data-testid="quote-notes-input" className="input-craft min-h-[110px]" placeholder={t("admin.editor.notesPh")} disabled={locked} value={notes} onChange={(e) => setNotes(e.target.value)} />
        <dl className="text-sm space-y-1.5 font-mono">
          <div className="flex justify-between"><dt className="font-sans text-ink/60">{t("quote.subtotal")}</dt><dd>{eur(subtotal)}</dd></div>
          <div className="flex justify-between"><dt className="font-sans text-ink/60">{t("quote.vat", { rate: vat })}</dt><dd>{eur(subtotal * vat / 100)}</dd></div>
          <div className="flex justify-between font-semibold"><dt className="font-sans">{t("quote.total")}</dt><dd>{eur(total)}</dd></div>
          {credit > 0 && <div className="flex justify-between text-forest"><dt className="font-sans">{t("admin.editor.creditShort")}</dt><dd>− {eur(credit)}</dd></div>}
          <div className="flex justify-between text-lg font-bold border-t border-craft pt-2"><dt className="font-sans">{t("admin.editor.due")}</dt><dd data-testid="quote-editor-due">{eur(due)}</dd></div>
          <div className="text-xs text-ink/55 font-sans">{pct >= 100 ? t("admin.editor.under") : t("admin.editor.over", { pct, amt: eur(due * pct / 100) })}</div>
        </dl>
      </div>
      {!locked && (
        <div className="mt-5 flex flex-wrap gap-3">
          <button data-testid="quote-save-draft-button" className="btn-secondary" disabled={busy} onClick={() => save(false)}><Save size={16} /> {t("admin.editor.saveDraft")}</button>
          <button data-testid="admin-quote-submit-button" className="btn-primary" disabled={busy || subtotal <= 0} onClick={() => save(true)}><Send size={16} /> {t("admin.editor.send")}</button>
        </div>
      )}
    </div>
  );
}
